import { Box, Typography, useMediaQuery, useTheme } from "@mui/material";
import { grey } from "@mui/material/colors";
import { OrganizationUserContext } from "contexts/Organization";
import { SubscriptionContext } from "contexts/Subscription";
import { useContext } from "react";
import {
  Plans,
  QUARTERLY_BASE_MONTHLY_PLAN,
  isQuarterlyPlan,
} from "schemas/payments";

import { useCheckout } from "components/StripeCheckout/useCheckout";
import { B2GO_PROMO_END_LABEL_WITH_YEAR } from "constants/influencer";
import {
  DISCOUNTED_PRO_PRICE,
  PRO_SALE_END,
  eligibleForProDiscount,
} from "features/Home/Banner/helpers";
import { useSubscription } from "utils/useSubscription";

const styles = {
  strike: {
    textDecoration: "line-through",
    color: "#7a9f9c",
    fontSize: 22,
  },
  strikeMobile: {
    textDecoration: "line-through",
    fontSize: 20,
    color: "#FFBAAB",
  },
};

interface Props {
  plan: Plans;
}

const PlansPrice = ({ plan }: Props) => {
  const theme = useTheme();
  const { currentOrg } = useContext(OrganizationUserContext);
  const isMobileScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const { plans, subscription } = useContext(SubscriptionContext);
  const { getPromoCodeForPlan } = useCheckout();
  const { getPrice } = useSubscription();
  const promoCode = getPromoCodeForPlan(plan);

  if (!plans) return <></>;

  const isCurrentPlan = subscription?.subscription?.planName === plan;
  const originalPrice = isCurrentPlan
    ? subscription?.subscription?.price!
    : Number(plans[plan]?.price);
  const discountedPrice = Number(getPrice(plan)) || originalPrice;
  const hasDiscounted = !isCurrentPlan && originalPrice > discountedPrice;
  const priceSuffix = "/mo";
  // Quarterly (B2GO) prices arrive from the backend as a 3-month total; show them per-month.
  const formatPrice = (value: number) =>
    isQuarterlyPlan(plan) ? (value / 3).toFixed(2) : value;

  if (isQuarterlyPlan(plan)) {
    const b2goMonthly = Number(plans[plan]?.price) / 3;
    const baseMonthlyPlan = QUARTERLY_BASE_MONTHLY_PLAN[plan];
    const regularMonthly = baseMonthlyPlan
      ? Number(plans[baseMonthlyPlan]?.price)
      : 0;
    const showStrike = regularMonthly > b2goMonthly;
    const b2goEndLabel = B2GO_PROMO_END_LABEL_WITH_YEAR;

    if (isMobileScreen) {
      return (
        <Box sx={{ fontSize: showStrike ? 20 : 24 }}>
          {showStrike && (
            <Box component={"span"} sx={styles.strikeMobile}>
              ${regularMonthly}
            </Box>
          )}
          <Box component={"span"}> ${b2goMonthly.toFixed(2)}</Box>
          <Box component={"span"} sx={{ color: grey[400] }}>
            {priceSuffix}
          </Box>
          {showStrike && (
            <Typography sx={{ fontSize: 11, color: grey[400] }}>
              until {b2goEndLabel} 🎁
            </Typography>
          )}
        </Box>
      );
    }

    return (
      <>
        {showStrike && (
          <Box sx={styles.strike}>
            <Box>
              ${regularMonthly}
              {priceSuffix}
            </Box>
          </Box>
        )}
        <Box>
          ${b2goMonthly.toFixed(2)}
          {priceSuffix}
        </Box>
        {showStrike && (
          <Typography sx={{ color: "#d95e58" }}>
            until {b2goEndLabel}
          </Typography>
        )}
      </>
    );
  }

  if (isMobileScreen) {
    if (
      !hasDiscounted &&
      plan === Plans.PRO &&
      eligibleForProDiscount(currentOrg, plans, subscription)
    ) {
      return (
        <Box sx={{ fontSize: hasDiscounted ? 20 : 24 }}>
          <Box component={"span"} sx={hasDiscounted ? styles.strikeMobile : {}}>
            $34
          </Box>

          <Box component={"span"}> ${DISCOUNTED_PRO_PRICE}</Box>
          <Box component={"span"} sx={{ color: grey[400] }}>
            /mo
          </Box>
          {hasDiscounted && (
            <Typography sx={{ fontSize: 11, color: grey[400] }}>
              until{" "}
              {PRO_SALE_END.toLocaleDateString("en-US", {
                month: "long",
                day: "numeric",
                year: "numeric",
              })}{" "}
              💥
            </Typography>
          )}
        </Box>
      );
    }

    return (
      <Box sx={{ fontSize: hasDiscounted ? 20 : 24 }}>
        <Box component={"span"} sx={hasDiscounted ? styles.strikeMobile : {}}>
          ${formatPrice(originalPrice)}
        </Box>

        {hasDiscounted && (
          <Box component={"span"}> ${formatPrice(Number(getPrice(plan)))}</Box>
        )}
        <Box component={"span"} sx={{ color: grey[400] }}>
          {priceSuffix}
        </Box>
        {hasDiscounted && promoCode?.dateEnding && (
          <Typography sx={{ fontSize: 11, color: grey[400] }}>
            until {promoCode?.dateEnding} 💥
          </Typography>
        )}
      </Box>
    );
  }

  return (
    <>
      <Box sx={hasDiscounted ? styles.strike : {}}>
        <Box>
          ${formatPrice(originalPrice)}
          {priceSuffix}
        </Box>
      </Box>
      {hasDiscounted && (
        <Box>
          ${formatPrice(Number(getPrice(plan)))}
          {priceSuffix}
        </Box>
      )}
      {promoCode?.dateEnding && hasDiscounted && (
        <Typography sx={{ color: "#d95e58" }}>
          until {promoCode?.dateEnding}
        </Typography>
      )}
    </>
  );
};

export default PlansPrice;
